import { type components, unwrap } from '@shared/api-client/index';
import { toastApiError } from '@shared/ui/toast/toast-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

import { api } from '../core/api';
import { type Member, membersKey, seatMembersKey } from '../members/members.hooks';
import { toastSeatLimitOrApiError } from '../members/seat-limit';

export type BillingStatus = components['schemas']['BillingStatusResponse'];
export type BillingMember = components['schemas']['BillingMember'];
export type Invoice = components['schemas']['InvoiceResponse'];
export type MemberStatus = components['schemas']['ChangeStatusRequest']['status'];

export const billingStatusKey = ['billing-status'] as const;
export const invoicesKey = ['billing-invoices'] as const;

/** The active workspace's plan, seat usage and next-payment figures. Admin-only on the api. */
export function useBillingStatus() {
	return useQuery({
		queryKey: billingStatusKey,
		queryFn: async () => unwrap(await api.GET('/v1/billing/status')),
		staleTime: 30_000,
	});
}

/**
 * The workspace's recent invoices for the in-app payment history. Admin-only on
 * the api; `enabled` gates it so it only fetches in the paid/configured state.
 */
export function useInvoices(enabled = true) {
	return useQuery({
		queryKey: invoicesKey,
		queryFn: async () => unwrap(await api.GET('/v1/billing/invoices')),
		enabled,
		staleTime: 30_000,
	});
}

/**
 * Reconcile the workspace's billing from Stripe immediately and flip the cached
 * status to the returned truth. Powers the checkout-return refresh. Quiet on
 * error — the throttled read + the Stripe webhook are the backstop. Admin-only.
 */
export function useReconcileBilling() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => unwrap(await api.POST('/v1/billing/reconcile')),
		onSuccess: (status) => {
			// Flip the cached status straight away, then refetch to settle any drift.
			queryClient.setQueryData(billingStatusKey, status);
			queryClient.invalidateQueries({ queryKey: billingStatusKey });
		},
	});
}

/**
 * On returning from Stripe Checkout (`?status=success`), reconcile once so the
 * page flips to paid immediately instead of waiting for the webhook or the next
 * throttled read, then strip the flag from the URL so a refresh doesn't re-run it.
 */
export function useReconcileOnCheckoutReturn() {
	const [searchParams, setSearchParams] = useSearchParams();
	const { mutate: reconcile } = useReconcileBilling();
	const fired = useRef(false);
	const returned = searchParams.get('status') === 'success';

	useEffect(() => {
		if (!returned || fired.current) {
			return;
		}
		fired.current = true;
		reconcile();
		const next = new URLSearchParams(searchParams);
		next.delete('status');
		setSearchParams(next, { replace: true });
	}, [returned, reconcile, searchParams, setSearchParams]);
}

/** Open a Stripe Checkout session and send the browser there to upgrade. Admin-only. */
export function useCheckout() {
	return useMutation({
		mutationFn: async () => unwrap(await api.POST('/v1/billing/checkout')),
		onError: toastApiError,
		onSuccess: ({ url }) => window.location.assign(url),
	});
}

/** Open the Stripe customer portal (card, invoices, cancel) and redirect there. Admin-only. */
export function useBillingPortal() {
	return useMutation({
		mutationFn: async () => unwrap(await api.POST('/v1/billing/portal')),
		onError: toastApiError,
		onSuccess: ({ url }) => window.location.assign(url),
	});
}

/**
 * Enable or disable a member's seat. Disabling frees a paid seat, enabling
 * consumes one, so both the billing figures and the seat list must refetch.
 * Optimistically flips the member's status in the seat cache so the Switch feels
 * instant, rolls back on error, and reconciles both queries from the api on
 * settle so concurrent toggles land on the server's truth. Admin-only.
 */
export function useSetMemberStatus() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ userId, status }: { userId: string; status: MemberStatus }) =>
			unwrap(
				await api.PATCH('/v1/members/{userId}/status', {
					params: { path: { userId } },
					body: { status },
				}),
			),
		onMutate: async ({ userId, status }) => {
			await queryClient.cancelQueries({ queryKey: seatMembersKey });
			const previous = queryClient.getQueryData<Member[]>(seatMembersKey);
			queryClient.setQueryData<Member[]>(seatMembersKey, (members) =>
				members?.map((member) => (member.userId === userId ? { ...member, status } : member)),
			);
			return { previous };
		},
		onError: (error, _variables, context) => {
			if (context?.previous) {
				queryClient.setQueryData(seatMembersKey, context.previous);
			}
			// Re-enabling a seat over the free cap surfaces the same upgrade nudge.
			toastSeatLimitOrApiError(error);
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: billingStatusKey });
			queryClient.invalidateQueries({ queryKey: membersKey });
		},
	});
}
